/**
 * Cambridge Summer Program config.
 * Design: Academic/Innovation theme with Cambridge colors.
 */

export interface CambridgeSummerHighlight {
    icon: string;
    title: string;
    description: string;
}

export interface CambridgeSummerBenefit {
    text: string;
}

export interface CambridgeSummerWhyJoinReason {
    icon: string;
    title: string;
    description: string;
    image: string;
}

export interface CambridgeSummerWeek {
    week: number;
    title: string;
    description: string;
    focusAreas: string[];
    includes?: string[];
}

export interface CambridgeSummerConfig {
    hero: {
        badge: string;
        title: string;
        subtitle: string;
        floatingCard: {
            label: string;
            location: string;
            format: string;
            registrationFeeLabel: string;
            earlyBirdEndDate?: string;
            normalPriceFormatted?: string;
        };
        backgroundImage: string;
    };
    introduction: {
        title: string;
        content: string[];
        details: {
            location: string;
            format: string;
        };
        sideImages: string;
    };
    highlights: CambridgeSummerHighlight[];
    whyJoin: {
        title: string;
        subheadline: string;
        twoWeekReasons: CambridgeSummerWhyJoinReason[];
        fourWeekReasons: CambridgeSummerWhyJoinReason[];
    };
    benefits: {
        title: string;
        items: CambridgeSummerBenefit[];
    };
    schedule: {
        headline: string;
        totalInstruction: string;
        sessions: string[];
        twoWeekProgram: {
            title: string;
            weeks: CambridgeSummerWeek[];
            culturalTrips?: string[];
        };
        fourWeekProgram: {
            title: string;
            weeks: CambridgeSummerWeek[];
            culturalTrips: string[];
        };
    };
}

export const cambridgeSummerConfig: CambridgeSummerConfig = {
    hero: {
        badge: "Cambridge Academic Excellence",
        title: "Cambridge Summer Program",
        subtitle:
            "An immersive academic and cultural experience combining English proficiency, AI literacy, entrepreneurship, and innovation in the heart of Cambridge, United Kingdom.",
        floatingCard: {
            label: "Location",
            location: "Cambridge, United Kingdom",
            format: "Academic + Mentorship + Cultural Immersion",
            registrationFeeLabel: "Program Fee",
            earlyBirdEndDate: undefined,
            normalPriceFormatted: undefined,
        },
        backgroundImage: "/images/cambridge-summer-school.jpg",
    },
    introduction: {
        title: "About the Program",
        content: [
            "This is an immersive academic and cultural experience hosted in Cambridge, United Kingdom, exclusively for students of New York Empire Academy.",
            "The programme combines English proficiency, Artificial Intelligence literacy, entrepreneurship, and a capstone innovation project within an internationally inspiring academic environment.",
            "Students receive instruction from Cambridge-affiliated educators, participate in structured workshops, engage in cultural university visits, and graduate with an official Certificate of Attendance issued by a Cambridge University College along with a Recommendation Letter from the Program Director.",
        ],
        details: {
            location: "Cambridge, United Kingdom",
            format: "Academic instruction + Mentorship + Cultural immersion",
        },
        sideImages: "/images/cambridge-summer-school.jpg",
    },
    highlights: [
        {
            icon: "MessageSquare",
            title: "Master Academic English with a Certified IELTS Examiner",
            description:
                "Students strengthen speaking confidence, pronunciation, academic writing, and presentation skills. Through diagnostic assessments and small-group instruction, they receive structured feedback designed to elevate real-world communication performance.",
        },
        {
            icon: "Cpu",
            title: "Explore Artificial Intelligence in a Practical Way",
            description:
                "Participants gain AI literacy without needing prior technical knowledge. They explore machine learning fundamentals, real-world AI applications, ethics, and future implications through hands-on case studies and guided discussions.",
        },
        {
            icon: "Lightbulb",
            title: "Build an Entrepreneurial Mindset",
            description:
                "Students learn how ideas turn into impactful ventures. The programme introduces business models, value propositions, leadership principles, teamwork dynamics, and persuasive pitching skills.",
        },
        {
            icon: "Award",
            title: "Develop and Present a Capstone Innovation Project",
            description:
                "In the final stage, students collaborate in teams to design a project that combines AI and entrepreneurship. They prepare a structured presentation, receive mentorship, and present formally on Final Presentation Day before receiving their certificates.",
        },
    ],
    whyJoin: {
        title: "Why Join the Cambridge Summer Program?",
        subheadline: "Intensive academic training meets global cultural immersion",
        twoWeekReasons: [
            {
                icon: "TrendingUp",
                title: "Academic Acceleration",
                description: "Strengthen English, explore AI, and develop entrepreneurial thinking in an intensive, high-impact format guided by expert instructors.",
                image: "/images/academic-acceleration.jpg"
            },
            {
                icon: "Globe",
                title: "Study in World-Class Environments",
                description: "Experience academic culture in Cambridge and visit leading institutions including the University of Cambridge and the University of Oxford.",
                image: "/images/world-class-environments.webp"
            },
            {
                icon: "Award",
                title: "Global Confidence & Competitive Edge",
                description: "Through supervised excursions and a final project presentation, build leadership, communication skills, and international exposure that enhance university applications.",
                image: "/images/global-confidence.jpg"
            }
        ],
        fourWeekReasons: [
            {
                icon: "Rocket",
                title: "Depth & Transformation",
                description: "Progress through English mastery, AI literacy, and entrepreneurship with structured practice, feedback, and refinement across four immersive weeks.",
                image: "/images/depth-transformation.webp"
            },
            {
                icon: "Brain",
                title: "Academic Tradition Meets Innovation",
                description: "Study within the Cambridge academic environment while exploring landmarks such as the British Museum and Buckingham Palace.",
                image: "/images/academic-tradition.jpg"
            },
            {
                icon: "GraduationCap",
                title: "Position Yourself for Leading Universities",
                description: "Complete a fully developed capstone project, gain international academic exposure, and graduate with credentials that strengthen global university readiness.",
                image: "/images/leading-universities.jpg"
            }
        ]
    },
    benefits: {
        title: "What You'll Gain",
        items: [
            { text: "Stronger academic English communication skills" },
            { text: "Foundational understanding of Artificial Intelligence and its applications" },
            { text: "Practical entrepreneurship and problem-solving experience" },
            { text: "Team collaboration and leadership development" },
            { text: "Public speaking and structured presentation skills" },
            { text: "Direct exposure to Cambridge academic culture" },
            { text: "Official Certificate of Attendance issued by a Cambridge University College" },
            { text: "Increased competitiveness for top university applications" },
        ],
    },
    schedule: {
        headline: "Program Schedule",
        totalInstruction: "4 Hours Per Day",
        sessions: ["2 Hours Morning Session", "2 Hours Afternoon Session"],
        twoWeekProgram: {
            title: "2-Week Program",
            weeks: [
                {
                    week: 1,
                    title: "English Proficiency & Artificial Intelligence",
                    description: "Morning and afternoon sessions focus on:",
                    focusAreas: [
                        "Academic and practical English communication",
                        "IELTS-based skill development",
                        "AI fundamentals and machine learning introduction",
                        "Real-world AI case studies",
                        "Ethics and future impact discussions",
                    ],
                    includes: [
                        "Diagnostic assessment at the beginning",
                        "Progress feedback at the end of the week",
                    ],
                },
                {
                    week: 2,
                    title: "Entrepreneurship, Innovation & Capstone Project",
                    description: "Students develop:",
                    focusAreas: [
                        "Idea generation and problem-solving",
                        "Business models and value creation",
                        "Pitching and storytelling",
                        "Leadership and teamwork",
                        "Capstone project development",
                        "Mentored preparation for final presentation",
                    ],
                    includes: [
                        "Team presentations",
                        "Certificate & Recommendation Letter award ceremony",
                    ],
                },
            ],
        },
        fourWeekProgram: {
            title: "4-Week Program",
            weeks: [
                {
                    week: 1,
                    title: "English Proficiency",
                    description: "Focus Areas:",
                    focusAreas: [
                        "Academic and practical English communication",
                        "Speaking confidence and pronunciation",
                        "Academic writing and presentation skills",
                        "Listening and real-world interaction",
                        "Small-group interactive instruction",
                    ],
                    includes: [
                        "Diagnostic assessment at the start",
                        "Progress feedback at the end of the week",
                    ],
                },
                {
                    week: 2,
                    title: "Artificial Intelligence (AI)",
                    description: "Focus Areas:",
                    focusAreas: [
                        "Introduction to AI and Machine Learning",
                        "Real-world applications across industries",
                        "Ethics and responsibility in AI",
                        "Future implications of emerging technologies",
                        "Hands-on activities and guided case studies",
                    ],
                    includes: ["No prior technical background required."],
                },
                {
                    week: 3,
                    title: "Entrepreneurship & Innovation",
                    description: "Focus Areas:",
                    focusAreas: [
                        "Idea generation and structured problem-solving",
                        "Business models and value propositions",
                        "Pitching and storytelling techniques",
                        "Leadership and teamwork dynamics",
                    ],
                    includes: ["Students begin forming teams and shaping their Capstone Project concepts."],
                },
                {
                    week: 4,
                    title: "Capstone Project & Presentation",
                    description: "Focus: Application, collaboration, execution.",
                    focusAreas: [
                        "Develop a project integrating AI and entrepreneurship",
                        "Refine structured presentations",
                        "Receive mentorship from instructors",
                        "Present formally on Final Presentation Day",
                    ],
                    includes: [
                        "Certificate awarded",
                        "Recommendation Letter issued",
                    ],
                },
            ],
            culturalTrips: [
                "London Day Trip - Cultural exploration and guided visits",
                "Oxford University Visit - Guided college tours and historic city walk",
                "Cambridge Cultural Experience - College visits at University of Cambridge, River Cam punting, Historic city tour",
                "Full-Day Outlet Shopping Experience",
            ],
        },
    },
};

export default cambridgeSummerConfig;
